import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Auth, idToken } from '@angular/fire/auth';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import {
  IDocumentLayoutsDocument,
  IDocumentLayoutSummary,
} from '../../../features/settings-new/models/documents/document-layout.interface';

@Injectable({ providedIn: 'root' })
export class DocumentLayoutsApiService {
  private readonly baseUrl: string = environment.gallixDocumentsApi || environment.gallixApi;

  constructor(
    private readonly http: HttpClient,
    private readonly auth: Auth,
  ) {}

  listPurchaseOrderLayouts(): Observable<IDocumentLayoutSummary[]> {
    return this.authorized().pipe(
      switchMap((headers: HttpHeaders) =>
        this.http.get<{ layouts: IDocumentLayoutSummary[] }>(`${this.baseUrl}/documents/layouts`, {
          headers,
          params: { doc_type: 'purchase_order' },
        }),
      ),
      map((response) => response.layouts ?? []),
    );
  }

  getSettings(storeId: string): Observable<IDocumentLayoutsDocument> {
    return this.authorized().pipe(
      switchMap((headers: HttpHeaders) =>
        this.http.get<IDocumentLayoutsDocument>(
          `${this.baseUrl}/documents/settings/${encodeURIComponent(storeId)}`,
          { headers },
        ),
      ),
    );
  }

  saveSettings(
    storeId: string,
    settings: IDocumentLayoutsDocument,
  ): Observable<IDocumentLayoutsDocument> {
    return this.authorized().pipe(
      switchMap((headers: HttpHeaders) =>
        this.http.put<IDocumentLayoutsDocument>(
          `${this.baseUrl}/documents/settings/${encodeURIComponent(storeId)}`,
          settings,
          { headers },
        ),
      ),
    );
  }

  previewPurchaseOrder(storeId: string, layoutKey: string, accentColour: string): Observable<Blob> {
    return this.authorized().pipe(
      switchMap((headers: HttpHeaders) =>
        this.http.post(
          `${this.baseUrl}/documents/purchase-order/preview`,
          { accent_colour: accentColour, layout_key: layoutKey, store_id: storeId },
          { headers, responseType: 'blob' },
        ),
      ),
      map((body: Blob) => new Blob([body], { type: 'application/pdf' })),
    );
  }

  private authorized(): Observable<HttpHeaders> {
    return idToken(this.auth).pipe(
      take(1),
      map((token: string | null) => new HttpHeaders({ Authorization: `Bearer ${token ?? ''}` })),
    );
  }
}
